import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Link } from '@tanstack/react-router'
import { User, MoreVertical, Eye, CheckCircle, XCircle, LogOut, Trash2, FileText, Lightbulb } from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

interface AdminPatientCardProps {
  patient: {
    id: string
    full_name: string
    status: 'active' | 'inactive' | 'discharged' | 'deleted'
    condition_description?: string | null
    birth_date?: string | null
    created_at: string
    _count: {
      reports: number
      insights: number
    }
  }
  onChangeStatus?: (id: string, status: 'active' | 'inactive' | 'discharged') => void
  onDelete?: (patient: { id: string; full_name: string }) => void
}

const statusLabels: Record<string, string> = {
  active: 'Ativo',
  inactive: 'Inativo',
  discharged: 'Alta',
  deleted: 'Excluido',
}

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-yellow-100 text-yellow-800',
  discharged: 'bg-gray-100 text-gray-800',
  deleted: 'bg-red-100 text-red-800',
}

function getAge(birthDate: string | null | undefined): string | null {
  if (!birthDate) return null
  const birth = new Date(birthDate)
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const monthDiff = today.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--
  }
  return `${age} anos`
}

export function AdminPatientCard({ patient, onChangeStatus, onDelete }: AdminPatientCardProps) {
  const age = getAge(patient.birth_date)

  return (
    <Card className={patient.status === 'discharged' ? 'opacity-60' : ''}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-muted rounded-full">
              <User className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Link
                  to="/patients/$patientId"
                  params={{ patientId: patient.id }}
                  className="font-medium hover:underline"
                >
                  {patient.full_name}
                </Link>
                <Badge className={statusColors[patient.status]}>
                  {statusLabels[patient.status]}
                </Badge>
              </div>
              {(age || patient.condition_description) && (
                <p className="text-sm text-muted-foreground">
                  {[age, patient.condition_description].filter(Boolean).join(' • ')}
                </p>
              )}
              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  {patient._count.reports} relatorios
                </span>
                <span className="flex items-center gap-1">
                  <Lightbulb className="h-3 w-3" />
                  {patient._count.insights} insights
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Desde {format(new Date(patient.created_at), "d 'de' MMMM, yyyy", { locale: ptBR })}
              </p>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to="/patients/$patientId" params={{ patientId: patient.id }}>
                  <Eye className="mr-2 h-4 w-4" />
                  Ver Detalhes
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {patient.status !== 'active' && onChangeStatus && (
                <DropdownMenuItem onClick={() => onChangeStatus(patient.id, 'active')}>
                  <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
                  Ativar
                </DropdownMenuItem>
              )}
              {patient.status === 'active' && onChangeStatus && (
                <DropdownMenuItem onClick={() => onChangeStatus(patient.id, 'inactive')}>
                  <XCircle className="mr-2 h-4 w-4 text-yellow-600" />
                  Desativar
                </DropdownMenuItem>
              )}
              {patient.status !== 'discharged' && onChangeStatus && (
                <DropdownMenuItem
                  onClick={() => onChangeStatus(patient.id, 'discharged')}
                >
                  <LogOut className="mr-2 h-4 w-4 text-orange-600" />
                  Dar Alta
                </DropdownMenuItem>
              )}
              {onDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDelete({ id: patient.id, full_name: patient.full_name })}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Excluir
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  )
}
